import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useUser } from '../../contexts/UserContext';
import { useSalon } from '../../contexts/SalonContext';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { formatarPreco } from '../../utils/priceUtils';
import { formatDate } from '../../utils/dateUtils';
// Importar as funções centralizadas de appointments
import { getAppointments, updateAppointment, cancelAppointment } from '../../firebase';
// Importar o componente AppointmentTimeline
import AppointmentTimeline from '../AppointmentTimeline';
// Importar o modal de detalhes do agendamento
import AppointmentDetailModal from '../admin/AppointmentDetailModal';
import { useParams } from 'react-router-dom';

/**
 * Dashboard para profissionais visualizarem suas informações e comissões
 * @param {Object} props Propriedades do componente
 * @param {Function} props.setProfessionalInfo Função para atualizar as informações do profissional no componente pai
 * @returns {JSX.Element} Componente de dashboard para profissionais
 */
const ProfessionalDashboard = ({ setProfessionalInfo: setParentProfessionalInfo }) => {
  const { selectedSalonId } = useUser(); // Obter salonId do contexto do usuário
  const { currentUser } = useAuth();
  const { salonConfig } = useSalon();
  
  // Estados principais
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [professionalId, setProfessionalId] = useState(null);
  const [professionalInfo, setProfessionalInfo] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [stats, setStats] = useState({
    pendingCommission: 0,
    paidCommission: 0,
    totalAppointments: 0,
    completedAppointments: 0,
    futureAppointments: 0
  });
  
  // Estados para visualização
  const [viewMode, setViewMode] = useState('tabela'); // 'tabela' ou 'calendario'
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  });
  
  // Estados para o modal
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  
  // Período de filtro para comissões
  const [period, setPeriod] = useState('month'); // 'week', 'month', 'year', 'all'
  
  // ==================== HANDLERS ====================
  // Defina handlers antes de usá-los em useCallback ou useMemo
  
  // Função para abrir o modal de detalhes do agendamento
  const handleOpenAppointmentDetail = useCallback((appointment) => {
    setSelectedAppointment(appointment);
    setIsDetailModalOpen(true);
  }, []);
  
  // Função para fechar o modal de detalhes
  const handleCloseDetailModal = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedAppointment(null);
  }, []);
  
  // Função para atualizar um agendamento
  const handleEventUpdate = useCallback(async (appointmentId, updateData) => {
    try {
      console.log(`[ProfessionalDashboard] Atualizando agendamento ${appointmentId}:`, updateData);
      const result = await updateAppointment(selectedSalonId, appointmentId, updateData);
      return !!result;
    } catch (error) {
      console.error('[ProfessionalDashboard] Erro ao atualizar agendamento:', error);
      return false;
    }
  }, [selectedSalonId]);

  // Função para bloquear um horário
  const handleTimeBlocked = useCallback((blockData) => {
    console.log('[ProfessionalDashboard] Bloqueando horário:', blockData);
    // Implementar lógica para bloquear horário
  }, []);

  // Função para mudar a visualização do calendário
  const handleViewChange = useCallback((newView) => {
    console.log('[ProfessionalDashboard] Mudando visualização para:', newView);
    // Atualizar preferências do usuário, se necessário
  }, []);
  
  // Função para lidar com a seleção de evento no calendário
  const handleSelectCalendarEvent = useCallback((event) => {
    console.log('Event selected:', event);
    if (event && event.appointment) {
      handleOpenAppointmentDetail(event.appointment);
    }
  }, [handleOpenAppointmentDetail]);
  
  // Função para carregar os dados - definida após os handlers que ela utiliza
  const loadData = useCallback(async () => {
    if (!currentUser || !selectedSalonId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Obter o ID do profissional a partir do userRoles
      const userRolesRef = doc(db, 'userRoles', currentUser.uid);
      const userRolesSnap = await getDoc(userRolesRef);
      
      if (!userRolesSnap.exists()) {
        setError('Informações do usuário não encontradas. Entre em contato com o administrador.');
        setLoading(false);
        return;
      }
      
      const userRolesData = userRolesSnap.data();
      const salonRole = userRolesData.salons?.find(s => s.salonId === selectedSalonId);
      
      if (!salonRole || !salonRole.professionalId) {
        setError('Seu perfil de profissional não está configurado corretamente.');
        setLoading(false);
        return;
      }
      
      const professionalId = salonRole.professionalId;
      
      // Buscar informações do profissional
      const professionalRef = doc(db, `salons/${selectedSalonId}/professionals`, professionalId);
      const professionalSnap = await getDoc(professionalRef);
      
      if (!professionalSnap.exists()) {
        setError('Dados do profissional não encontrados.');
        setLoading(false);
        return;
      }
      
      const professionalData = professionalSnap.data();
      const professionalInfo = {
        id: professionalSnap.id,
        ...professionalData
      };
      
      // Atualizar o estado local
      setProfessionalInfo(professionalInfo);
      
      // Atualizar o estado no componente pai, se a função estiver disponível
      if (setParentProfessionalInfo) {
        setParentProfessionalInfo(professionalInfo);
      }
      
      // Buscar agendamentos usando a função centralizada
      let appointmentsData = [];
      try {
        const filters = { professionalId };
        appointmentsData = await getAppointments(selectedSalonId, filters);
        setAppointments(appointmentsData);
      } catch (appErr) {
        console.error(`[ProfessionalDashboard] Erro ao carregar agendamentos:`, appErr);
      }
      
      // Buscar comissões do profissional
      let commissionsData = [];
      try {
        const periodStartDate = new Date();
        if (period === 'week') {
          periodStartDate.setDate(periodStartDate.getDate() - 7);
        } else if (period === 'month') {
          periodStartDate.setMonth(periodStartDate.getMonth() - 1);
        } else if (period === 'year') {
          periodStartDate.setFullYear(periodStartDate.getFullYear() - 1);
        } else if (period === 'all') {
          periodStartDate.setFullYear(2000);
        }
        
        const commissionsRef = collection(db, `salons/${selectedSalonId}/commissions`);
        let commissionsQuery;
        
        if (period !== 'all') {
          commissionsQuery = query(
            commissionsRef,
            where('professionalId', '==', professionalId),
            where('data', '>=', periodStartDate),
            orderBy('data', 'desc')
          );
        } else {
          commissionsQuery = query(
            commissionsRef,
            where('professionalId', '==', professionalId),
            orderBy('data', 'desc')
          );
        }
        
        const commissionsSnap = await getDocs(commissionsQuery);
        commissionsData = commissionsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setCommissions(commissionsData);
      } catch (commErr) {
        console.error(`[ProfessionalDashboard] Erro ao carregar comissões:`, commErr);
      }
      
      // Calcular estatísticas
      try {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const pendingCommission = commissionsData
          .filter(c => !c.pago)
          .reduce((sum, c) => sum + (c.valor || 0), 0);
          
        const paidCommission = commissionsData
          .filter(c => c.pago)
          .reduce((sum, c) => sum + (c.valor || 0), 0);
          
        const completedAppointments = appointmentsData
          .filter(a => a.status === 'realizado')
          .length;
          
        const futureAppointments = appointmentsData
          .filter(a => {
            const appointmentDate = new Date(a.data);
            return appointmentDate >= today && ['agendado', 'confirmado'].includes(a.status);
          })
          .length;
          
        setStats({
          pendingCommission,
          paidCommission,
          totalAppointments: appointmentsData.length,
          completedAppointments,
          futureAppointments
        });
      } catch (statsErr) {
        console.error(`[ProfessionalDashboard] Erro ao calcular estatísticas:`, statsErr);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('[ProfessionalDashboard] Erro ao carregar dados do profissional:', err);
      setError('Erro ao carregar dados do profissional: ' + err.message);
      setLoading(false);
    }
  }, [currentUser, selectedSalonId, period, setParentProfessionalInfo]);
  
  // Funções que dependem de loadData devem ser declaradas depois dela
  
  // Função para remarcar agendamento
  const handleReschedule = useCallback(async (appointmentId, newData) => {
    try {
      const result = await updateAppointment(selectedSalonId, appointmentId, newData);
      if (result) {
        await loadData();
        return true;
      }
      return false;
    } catch (error) {
      console.error('[ProfessionalDashboard] Erro ao remarcar agendamento:', error);
      return false;
    }
  }, [selectedSalonId, loadData]);
  
  // Função para cancelar agendamento
  const handleCancelAppointment = useCallback(async (appointmentId) => {
    try {
      const result = await cancelAppointment(selectedSalonId, appointmentId);
      if (result) {
        await loadData();
        return true;
      }
      return false;
    } catch (error) {
      console.error('[ProfessionalDashboard] Erro ao cancelar agendamento:', error);
      return false;
    }
  }, [selectedSalonId, loadData]);
  
  // Função para editar serviços do agendamento
  const handleEditServices = useCallback(async (appointmentId, newServices) => {
    try {
      const result = await updateAppointment(selectedSalonId, appointmentId, {
        servicos: newServices
      });
      if (result) {
        await loadData();
        return true;
      }
      return false;
    } catch (error) {
      console.error('[ProfessionalDashboard] Erro ao editar serviços:', error);
      return false;
    }
  }, [selectedSalonId, loadData]);
  
  // Função para marcar agendamento como pago
  const handleMarkAsPaid = useCallback(async (appointmentId, updateData = {}) => {
    try {
      const dataToUpdate = {
        pago: true,
        ...updateData
      };
      
      const result = await updateAppointment(selectedSalonId, appointmentId, dataToUpdate);
      if (result) {
        await loadData();
        return true;
      }
      return false;
    } catch (error) {
      console.error('[ProfessionalDashboard] Erro ao marcar como pago:', error);
      return false;
    }
  }, [selectedSalonId, loadData]);
  
  const handleChangePeriod = useCallback((newPeriod) => {
    setPeriod(newPeriod);
  }, []);
  
  // ==================== MEMOIZAÇÕES ====================
  // Agora as memoizações podem ser declaradas DEPOIS de todos os handlers
  
  // Memoização dos professionals para AppointmentTimeline
  const timelineProfessionals = useMemo(() => {
    return professionalInfo ? [professionalInfo] : [];
  }, [professionalInfo]);
  
  // Memoização de callbacks para o AppointmentTimeline
  const timelineHandlers = useMemo(() => ({
    onEventUpdate: handleEventUpdate,
    onTimeBlocked: handleTimeBlocked,
    onViewChange: handleViewChange,
    onSelectEvent: handleSelectCalendarEvent
  }), [handleEventUpdate, handleTimeBlocked, handleViewChange, handleSelectCalendarEvent]);
  
  // ==================== EFEITOS ====================
  
  // Efeito para carregar dados 
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Debug: monitorar atualizações de appointments
  useEffect(() => {
    console.log('Dashboard atualizado com', appointments.length, 'agendamentos');
  }, [appointments.length]);
  
  // ==================== RENDERIZAÇÃO ====================
  
  if (loading) {
    return (
      <div className="p-4">
        <div className="w-full max-w-7xl mx-auto">
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-6 py-1">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-4">
                  <div className="h-20 bg-gray-200 rounded col-span-1"></div>
                  <div className="h-20 bg-gray-200 rounded col-span-1"></div>
                  <div className="h-20 bg-gray-200 rounded col-span-1"></div>
                </div>
                <div className="h-4 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-4">
        <div className="w-full max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md">
            <h3 className="text-lg font-medium mb-2">Erro ao carregar dados</h3>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-4">
      <div className="w-full max-w-7xl mx-auto">
        {/* Cabeçalho com informações do profissional */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Olá, {professionalInfo?.nome || currentUser?.displayName || 'Profissional'}
              </h1>
              <p className="text-gray-600 mt-1">
                {salonConfig?.nome || 'Seu Salão'}
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <div className="text-sm text-gray-500">Comissão a receber</div>
              <div className="text-2xl font-bold text-green-600">{formatarPreco(stats.pendingCommission)}</div>
            </div>
          </div>
        </div>
        
        {/* Cards de estatísticas */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="text-sm text-gray-500">Agendamentos Futuros</div>
            <div className="text-3xl font-bold mt-2">{stats.futureAppointments}</div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="text-sm text-gray-500">Atendimentos Realizados</div>
            <div className="text-3xl font-bold mt-2">{stats.completedAppointments}</div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="text-sm text-gray-500">Comissão Total Recebida</div>
            <div className="text-3xl font-bold mt-2">{formatarPreco(stats.paidCommission)}</div>
          </div>
        </div>
        
        {/* Comissões Pendentes */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Comissões</h2>
            <div className="flex space-x-2">
              <button 
                onClick={() => handleChangePeriod('week')}
                className={`px-3 py-1 rounded-md text-sm ${period === 'week' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                7 dias
              </button>
              <button 
                onClick={() => handleChangePeriod('month')}
                className={`px-3 py-1 rounded-md text-sm ${period === 'month' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                30 dias
              </button>
              <button 
                onClick={() => handleChangePeriod('year')}
                className={`px-3 py-1 rounded-md text-sm ${period === 'year' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                12 meses
              </button>
              <button 
                onClick={() => handleChangePeriod('all')}
                className={`px-3 py-1 rounded-md text-sm ${period === 'all' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Todos
              </button>
            </div>
          </div>
          
          {commissions.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              Nenhuma comissão encontrada para o período selecionado.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor
                    </th>
                    <th className="px-6 py-3 bg-gray-50 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {commissions.map(commission => (
                    <tr key={commission.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(commission.data?.toDate ? commission.data.toDate() : commission.data)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {commission.cliente || 'Cliente'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                        {formatarPreco(commission.valor)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          commission.pago ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {commission.pago ? 'Pago' : 'Pendente'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal de detalhes do agendamento */}
        {isDetailModalOpen && selectedAppointment && (
          <AppointmentDetailModal
            isOpen={isDetailModalOpen}
            onClose={handleCloseDetailModal}
            appointmentId={selectedAppointment.id}
            salonId={selectedSalonId}
            appointment={selectedAppointment}
            onReschedule={handleReschedule}
            onEditServices={handleEditServices}
            onCancel={handleCancelAppointment}
            onMarkAsPaid={handleMarkAsPaid}
          />
        )}
        
      </div>
    </div>
  );
};

export default ProfessionalDashboard; 